import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  LinearProgress,
  Avatar,
  Tooltip,
  Button,
  Divider,
  IconButton,
  Popover,
  Stack,
} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ChatIcon from '@mui/icons-material/Chat';
import TimelineIcon from '@mui/icons-material/Timeline';
import ArticleIcon from '@mui/icons-material/Article';

import DescriptionIcon from '@mui/icons-material/Description';         // paper file
import SendIcon from '@mui/icons-material/Send';                       // presentation or submitted
import AssessmentIcon from '@mui/icons-material/Assessment';           // estimate
import CheckCircleIcon from '@mui/icons-material/CheckCircle';         // sales ready / supplier confirmed
import LocalShippingIcon from '@mui/icons-material/LocalShipping';     // shipped
import InventoryIcon from '@mui/icons-material/Inventory';             // received
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';   // ready for billing
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';         // billed
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'; // closed
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import "./OrderProgressBar.css";
import ChatBox from "./ChatBox";
import { useParams } from "react-router-dom";

/**
 * Optional docsData prop shape (all arrays are optional):
 * {
 *   orderId: string|number,
 *   presentations: [{ presentationId, id, presentationDate, ... }],
 *   salesOrders:   [{ estimationId, id, status, orderDate, ... }],
 *   purchaseOrders:[{ salesOrderId, ... }],
 *   invoices:      [...],
 *   bills:         [...]
 * }
 */
function OrderProgressBar({
  orderStatus,
  assignedUsers,
  allUsers = [],
  docsData = {},
  notesForChat = [],
  renderCart,
  getWorkflowLabel = (_group, status) => status,
  getWorkflowTooltip = () => "",
}) {
  const { orderId: orderIdFromRoute, instanceUniqueId } = useParams();
  const [expanded, setExpanded] = useState(false);
  const [soAnchorEl, setSoAnchorEl] = useState(null);
  const [poAnchorEl, setPoAnchorEl] = useState(null);

  const orderId = docsData.orderId ?? orderIdFromRoute;

  const openInstancePath = (relativePath) => {
    const normalized = String(relativePath || "").startsWith("/")
      ? relativePath
      : `/${relativePath || ""}`;
    if (!instanceUniqueId) return;
    window.open(`/instance/${instanceUniqueId}${normalized}`, "_blank");
  };

  // Toggle panel open/close
  const togglePanel = (panel) => {
    setExpanded(prev => (prev === panel ? false : panel));
  };

  // Order status and progress
  const salesOrderStatus = orderStatus?.SO || "";
  const purchaseOrderStatus = orderStatus?.PO ?? null;

  const salesStages = [
    "Quote Requested",
    "Waiting",
    "Presentation Draft",
    "Presentation Sent",
    "Estimate Draft",
    "Estimate Sent",
    "Agreement Pending",
    "Agreement Sent",
    "Client Change Requested",
    "Client Approved",
    "In Production",
    "Shipped",
    "Ready to be Invoiced",
    "Invoiced",
    "Closed",
  ];
  const purchaseStages = [
    "Created",
    "Open",
    "Follow Up",
    "Submitted",
    "Confirmed",
    "Shipped",
    "Partially Received",
    "Received",
    "Production Scheduled",
    "In Progress",
    "Production Complete",
    "Ready For Billing",
    "Billed",
    "Closed"
  ];

  const LEGACY_SO_STAGE_MAP = {
    lead: "Quote Requested",
    "quote requested": "Quote Requested",
    "quote_requested": "Quote Requested",
    waiting: "Waiting",
    presentation: "Presentation Draft",
    "presentation draft": "Presentation Draft",
    "presentation sent": "Presentation Sent",
    "presentation_sent": "Presentation Sent",
    "estimate draft": "Estimate Draft",
    "estimate sent": "Estimate Sent",
    "estimate_sent": "Estimate Sent",
    "sales order ready": "Client Approved",
    "customer signed": "Client Approved",
    completed: "Client Approved",
    "agreement sent": "Agreement Sent",
    "sent for agreement": "Agreement Pending",
    "agreement pending": "Agreement Pending",
    rejected: "Client Change Requested",
    "changes requested": "Client Change Requested",
    "client change requested": "Client Change Requested",
    "in production": "In Production",
    "ready to be invoiced": "Ready to be Invoiced",
    "ready to invoice": "Ready to be Invoiced",
    invoiced: "Invoiced",
    paid: "Invoiced",
    "paid in full": "Invoiced",
    closed: "Closed",
  };

  const normalizeSalesStage = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return "";
    const mapped = LEGACY_SO_STAGE_MAP[raw.toLowerCase()];
    return mapped || raw;
  };

  const salesIconMap = {
    "Quote Requested":   <DescriptionIcon sx={{ fontSize: 20 }} />,
    "Waiting": <TimelineIcon sx={{ fontSize: 20 }} />,
    "Presentation Draft": <DescriptionIcon sx={{ fontSize: 20 }} />,
    "Presentation Sent": <SendIcon        sx={{ fontSize: 20 }} />,
    "Estimate Draft":    <AssessmentIcon  sx={{ fontSize: 20 }} />,
    "Estimate Sent":     <AssessmentIcon  sx={{ fontSize: 20 }} />,
    "Agreement Pending": <SendIcon sx={{ fontSize: 20 }} />,
    "Agreement Sent":    <SendIcon sx={{ fontSize: 20 }} />,
    "Client Change Requested": <AssessmentIcon sx={{ fontSize: 20 }} />,
    "Client Approved":   <CheckCircleIcon sx={{ fontSize: 20 }} />,
    "In Production":     <PrecisionManufacturingIcon sx={{ fontSize: 20 }} />,
    "Shipped":           <LocalShippingIcon sx={{ fontSize: 20 }} />,
    "Ready to be Invoiced": <AccountBalanceIcon sx={{ fontSize: 20 }} />,
    "Invoiced":          <ReceiptLongIcon sx={{ fontSize: 20 }} />,
    "Closed":            <CheckCircleOutlineIcon sx={{ fontSize: 20 }} />,
  };

  const purchaseIconMap = {
    Created:                 <DescriptionIcon            sx={{ fontSize: 20 }} />,
    Submitted:               <SendIcon                   sx={{ fontSize: 20 }} />,
    Confirmed:               <CheckCircleIcon            sx={{ fontSize: 20 }} />,
    "Production Scheduled": <PrecisionManufacturingIcon sx={{ fontSize: 20 }} />,
    "Production Complete": <PrecisionManufacturingIcon sx={{ fontSize: 20 }} />,
    "Follow Up":            <TimelineIcon               sx={{ fontSize: 20 }} />,
    Open:                    <ShoppingCartIcon           sx={{ fontSize: 20 }} />,
    Shipped:                 <LocalShippingIcon          sx={{ fontSize: 20 }} />,
    Received:                <InventoryIcon              sx={{ fontSize: 20 }} />,
    "Partially Received":  <PrecisionManufacturingIcon sx={{ fontSize: 20 }} />,
    "Ready For Billing":    <AccountBalanceIcon         sx={{ fontSize: 20 }} />,
    Billed:                  <ReceiptLongIcon            sx={{ fontSize: 20 }} />,
    Closed:                  <CheckCircleOutlineIcon     sx={{ fontSize: 20 }} />
  };
  
  const normalizedSalesOrderStatus = normalizeSalesStage(salesOrderStatus);
  const salesIndex = salesStages.indexOf(normalizedSalesOrderStatus);
  const salesProgress = salesIndex >= 0 ? ((salesIndex + 1) / salesStages.length) * 100 : 0;

  const normalizePurchaseStage = (value) => {
    const raw = String(value || '').trim().toLowerCase();
    if (!raw) return '';
    if (raw === 'created') return 'Created';
    if (raw === 'submitted') return 'Submitted';
    if (raw === 'supplier confirmed') return 'Confirmed';
    if (raw === 'confirmed') return 'Confirmed';
    if (raw === 'follow up' || raw === 'follow-up' || raw === 'follow_up') return 'Follow Up';
    if (raw === 'open') return 'Open';
    if (raw === 'shipped from supplier') return 'Shipped';
    if (raw === 'shipped') return 'Shipped';
    if (raw === 'production complete' || raw === 'po-prod-complete') return 'Production Complete';
    if (raw === 'in production') return 'Partially Received';
    if (raw === 'partially received' || raw === 'partial received' || raw === 'partially_received') return 'Partially Received';
    if (raw === 'confirmed by shipping') return 'Received';
    if (raw === 'received') return 'Received';
    if (raw === 'ready for billing' || raw === 'ready_for_billing') return 'Ready For Billing';
    if (raw === 'billed') return 'Billed';
    if (raw === 'closed') return 'Closed';
    const direct = purchaseStages.find((stage) => stage.toLowerCase() === raw);
    return direct || '';
  };

  const purchaseIndex = purchaseOrderStatus != null
    ? purchaseStages.indexOf(normalizePurchaseStage(purchaseOrderStatus))
    : -1;
  const purchaseProgress = purchaseIndex >= 0
    ? ((purchaseIndex + 1) / purchaseStages.length) * 100
    : 0;

  // ---------- Helpers to detect "available" + pick latest ----------
  const arr = (x) => Array.isArray(x) ? x : [];

  const pickLatestByDates = (items, keys = []) => {
    if (!items?.length) return null;
    const parseDate = (v) => {
      const d = new Date(v);
      return Number.isNaN(d.getTime()) ? 0 : d.getTime();
    };
    const score = (it) => {
      for (const k of keys) {
        const v = it?.[k];
        if (v) return parseDate(v);
      }
      // fallback to createdAt/updatedAt if present
      return Math.max(parseDate(it?.createdAt), parseDate(it?.updatedAt));
    };
    return [...items].sort((a,b) => score(b) - score(a))[0] || null;
  };

  // We’ll compute docs availability from props (if given)
  const {
    presentations = [],
    salesOrders = [],
    purchaseOrders = [],
    invoices = [],
    bills = [],
  } = docsData;

  const latestPresentation = useMemo(
    () => pickLatestByDates(arr(presentations), ["presentationDate", "createdAt", "updatedAt"]),
    [presentations]
  );
  const hasPresentation = !!latestPresentation;

  const estimatesOnly = useMemo(
    () => arr(salesOrders).filter(so => ["1", "1_sent"].includes(String(so?.status))),
    [salesOrders]
  );
  const latestEstimate = useMemo(
    () => pickLatestByDates(estimatesOnly, ["orderDate", "createdAt", "updatedAt"]),
    [estimatesOnly]
  );
  const hasEstimate = estimatesOnly.length > 0;

  const sosOnly = useMemo(
    () => arr(salesOrders).filter(so => ["2","2_sent","3",2,3].includes(so?.status)),
    [salesOrders]
  );
  const latestSO = useMemo(
    () => pickLatestByDates(sosOnly, ["orderDate", "createdAt", "updatedAt"]),
    [sosOnly]
  );
  const hasSO = sosOnly.length > 0;

  // Purchase orders available when we actually have any POs,
  // OR your OrderStatus.PO is not null (i.e., PO stage started).
  const hasPOs = (arr(purchaseOrders).length > 0) || (purchaseOrderStatus != null);
  const hasGeneratedPurchaseOrders = arr(purchaseOrders).some((po) => po?.isDeleted !== true);

  const formatDateTime = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString();
  };

  const currentSoLabel = normalizedSalesOrderStatus
    ? getWorkflowLabel("SO", normalizedSalesOrderStatus)
    : "—";
  const currentPoCanonical = normalizePurchaseStage(purchaseOrderStatus) || purchaseOrderStatus || "";
  const currentPoLabel = currentPoCanonical
    ? getWorkflowLabel("PO", currentPoCanonical)
    : "PO stage not reached";

  const currentSoTooltip = useMemo(() => {
    const base = normalizedSalesOrderStatus
      ? getWorkflowTooltip("SO", normalizedSalesOrderStatus)
      : "";
    const details = [];
    if (normalizedSalesOrderStatus === "Presentation Sent") {
      const sentAt = formatDateTime(latestPresentation?.presentationDate || latestPresentation?.createdAt);
      if (sentAt) details.push(`Sent on ${sentAt}`);
      if (docsData?.orderEmail) details.push(`Sent to ${docsData.orderEmail}`);
    }
    if (normalizedSalesOrderStatus === "Estimate Sent") {
      const sentAt = formatDateTime(latestEstimate?.orderDate || latestEstimate?.createdAt);
      if (sentAt) details.push(`Estimate sent on ${sentAt}`);
    }
    if (normalizedSalesOrderStatus === "Client Approved") {
      const approvedAt = formatDateTime(latestSO?.orderDate || latestSO?.updatedAt);
      if (approvedAt) details.push(`Approved on ${approvedAt}`);
    }
    return [base, ...details].filter(Boolean).join(" • ");
  }, [normalizedSalesOrderStatus, getWorkflowTooltip, latestPresentation, latestEstimate, latestSO, docsData?.orderEmail]);

  // Match PO set tied to the latest SO when possible (optional)
  const poForLatestSO = useMemo(() => {
    if (!latestSO) return null;
    const soId = latestSO.estimationId ?? latestSO.id;
    const match = arr(purchaseOrders).find(po => Number(po?.salesOrderId) === Number(soId));
    return match || purchaseOrders?.[0] || null;
  }, [purchaseOrders, latestSO]);

  const latestInvoice = useMemo(
    () => pickLatestByDates(arr(invoices), ["invoiceDate", "createdAt", "updatedAt"]),
    [invoices]
  );

  const resolveStripeInvoiceUrl = (invoiceLike) => {
    if (!invoiceLike || typeof invoiceLike !== "object") return "";
    const candidates = [
      invoiceLike.stripeHostedInvoiceUrl,
      invoiceLike.hostedInvoiceUrl,
      invoiceLike.invoiceUrl,
      invoiceLike.stripeInvoiceUrl,
      invoiceLike.paymentLink,
      invoiceLike.checkoutUrl,
      invoiceLike.stripeUrl,
      invoiceLike.url,
    ];
    return candidates.find((value) => typeof value === "string" && /^https?:\/\//i.test(value)) || "";
  };

  const stripeInvoiceUrl = resolveStripeInvoiceUrl(latestInvoice);

  const currentPoTooltip = useMemo(() => {
    if (!currentPoCanonical) return "";
    const base = getWorkflowTooltip("PO", currentPoCanonical);
    const details = [];
    if (currentPoCanonical === "Shipped") {
      const shippedAt = formatDateTime(poForLatestSO?.dateShipped || poForLatestSO?.updatedAt);
      if (shippedAt) details.push(`Shipped on ${shippedAt}`);
    }
    if (currentPoCanonical === "Ready For Billing") {
      const targetAt = formatDateTime(poForLatestSO?.nextActionDate);
      if (targetAt) details.push(`Billing target ${targetAt}`);
    }
    return [base, ...details].filter(Boolean).join(" • ");
  }, [currentPoCanonical, getWorkflowTooltip, poForLatestSO]);

  // Invoices & Bills (basic heuristics if you don’t have arrays yet)
  const hasInvoices = arr(invoices).length > 0;
  const poStageStarted = Boolean(currentPoCanonical || purchaseOrderStatus);
  const hasBills = arr(bills).length > 0
    || hasGeneratedPurchaseOrders
    || poStageStarted;

  // ---------- Openers (centralized) ----------
  const openDoc = (type) => {
    switch (type) {
      case 'presentation': {
        if (!hasPresentation) return;
        const pid = latestPresentation.presentationId ?? latestPresentation.id;
        openInstancePath(`/presentations/${pid}`);
        break;
      }
      case 'estimate': {
        if (!hasEstimate) return;
        const eid = latestEstimate.estimationId ?? latestEstimate.id;
        openInstancePath(`/sales/orders/${orderId}/estimation/${eid}`);
        break;
      }
      case 'so': {
        if (!hasSO) return;
        const sid = latestSO.estimationId ?? latestSO.id;
        openInstancePath(`/sales/orders/${orderId}/sales-order/${sid}`);
        break;
      }
      case 'po': {
        if (!hasPOs) return;
        // If we matched a PO group for the latest SO, use its salesOrderId; otherwise fall back to SO.
        const soIdForPO =
          poForLatestSO?.salesOrderId ??
          latestSO?.estimationId ??
          latestSO?.id;
        if (soIdForPO) {
          openInstancePath(`/sales/orders/${orderId}/pos-proofs/${soIdForPO}`);
        } else {
          // fallback to PO home if needed
          openInstancePath(`/sales/orders/${orderId}/purchase-order-home`);
        }
        break;
      }
      case 'invoice': {
        // Route to the Invoice tab for this order
        openInstancePath(`/sales/orders/${orderId}/invoice`);
        break;
      }
      case 'bill': {
        // Route to the Bills tab for this order
        openInstancePath(`/sales/orders/${orderId}/bills`);
        break;
      }
      default:
        break;
    }
  };

  // ---------- Docs (now computed dynamically) ----------
  const soQuickActions = [];
  if (["Ready to be Invoiced", "Invoiced"].includes(normalizedSalesOrderStatus)) {
    soQuickActions.push({ label: "Open Invoice tab", onClick: () => openDoc("invoice") });
    soQuickActions.push({ label: "Open Sales Order", onClick: () => openDoc("so") });
    if (stripeInvoiceUrl) soQuickActions.push({ label: "Open Stripe invoice", onClick: () => window.open(stripeInvoiceUrl, "_blank") });
  }

  const poQuickActions = [];
  if (["Ready For Billing", "Billed"].includes(currentPoCanonical)) {
    poQuickActions.push({ label: "Open Invoice tab", onClick: () => openDoc("invoice") });
    poQuickActions.push({ label: "Open Sales Order", onClick: () => openDoc("so") });
    poQuickActions.push({ label: "Open Bills", onClick: () => openDoc("bill") });
    if (stripeInvoiceUrl) poQuickActions.push({ label: "Open Stripe invoice", onClick: () => window.open(stripeInvoiceUrl, "_blank") });
  }

  const renderJourneyPopover = ({ anchorEl, onClose, label, tooltip, actions = [] }) => (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
    >
      <Box sx={{ p: 1.5, maxWidth: 360 }}>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{label}</Typography>
        <Typography variant="body2" sx={{ opacity: 0.85 }}>{tooltip || "No additional details yet."}</Typography>
        {actions.length > 0 && (
          <>
            <Divider sx={{ my: 1 }} />
            <Typography variant="caption" sx={{ opacity: 0.8, display: "block", mb: 0.75 }}>Quick actions</Typography>
            <Stack spacing={0.75}>
              {actions.map((action) => (
                <Button
                  key={action.label}
                  size="small"
                  variant="outlined"
                  endIcon={<OpenInNewIcon fontSize="inherit" />}
                  onClick={() => { action.onClick(); onClose(); }}
                  sx={{ justifyContent: "space-between" }}
                >
                  {action.label}
                </Button>
              ))}
            </Stack>
          </>
        )}
      </Box>
    </Popover>
  );

  const docs = [
    {
      label: 'Presentation',
      icon: <SendIcon sx={{ fontSize: 48 }} />,
      available: hasPresentation,
      onClick: () => openDoc('presentation'),
    },
    {
      label: 'Estimate',
      icon: <AssessmentIcon sx={{ fontSize: 48 }} />,
      available: hasEstimate,
      onClick: () => openDoc('estimate'),
    },
    {
      label: 'Sales Order',
      icon: <CheckCircleIcon sx={{ fontSize: 48 }} />,
      available: hasSO,
      onClick: () => openDoc('so'),
    },
    {
      label: 'Purchase Orders',
      icon: <InventoryIcon sx={{ fontSize: 48 }} />,
      available: hasPOs,
      onClick: () => openDoc('po'),
    },
    {
      label: 'Bill',
      icon: <AccountBalanceIcon sx={{ fontSize: 48 }} />,
      available: hasBills,
      onClick: () => openDoc('bill'),
    },
    {
      label: 'Invoice',
      icon: <ReceiptLongIcon sx={{ fontSize: 48 }} />,
      available: hasInvoices,
      onClick: () => openDoc('invoice'),
    },
  ];

  // Team splitting
  const salesTeam = (assignedUsers || []).filter(
    (u) => u.role === "order-rep" || u.role === "client-rep"
  );
  const purchaseTeam = (assignedUsers || []).filter(
    (u) => u.role !== "order-rep" && u.role !== "client-rep"
  );

  // Avatar renderer
  const renderAvatar = (user) => {
    let userData = user;
    if (!user.profilePicture) {
      const full = allUsers.find((u) => u.id === user.id);
      if (full) userData = { ...user, ...full };
    }
    const defaultUrl = "/elite-core-placeholder-avatar.png";
    let picUrl = defaultUrl;
    if (userData.profilePicture) {
      let pic = userData.profilePicture;
      try { if (typeof pic === "string") pic = JSON.parse(pic); } catch {}
      if (Array.isArray(pic) && pic.length) {
        const sel = pic.find((p) => p.selected) || pic[0];
        if (sel.url) picUrl = sel.url.startsWith("uploads")
          ? `${process.env.REACT_APP_NODEJS_SERVER_URL}/${sel.url}`
          : sel.url;
      } else if (typeof pic === "string") {
        picUrl = pic.startsWith("uploads")
          ? `${process.env.REACT_APP_NODEJS_SERVER_URL}/${pic}`
          : pic;
      }
    }
    const initials = `${userData.first_name?.[0] || ""}${userData.last_name?.[0] || ""}`.toUpperCase();
    return (
      <Tooltip key={userData.id} title={`${userData.first_name} ${userData.last_name} (${userData.role})`}>
        <Avatar
          sx={{ width: 50, height: 50, mr: 1, bgcolor: "grey.300" }}
          src={picUrl === defaultUrl ? null : picUrl}
        >
          {picUrl === defaultUrl ? initials || "?" : null}
        </Avatar>
      </Tooltip>
    );
  };

  return (
    <Box className="order-progress-bar" sx={{ width: "100%", mb: 0 }}>
      {/* Tabs Header */}
      <Box className="progress-header-row" sx={{gap: 0}}>
        {/* Overview Tab */}
        <Box
          sx={{
            width: "33%",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
          className="overview-header-tab"
          onClick={() => togglePanel('overview')}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <TimelineIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Project Journey</Typography>
          </Box>
          {expanded === 'overview' ? <RemoveIcon /> : <AddIcon />}
        </Box>
  
        {/* Docs Tab */}
        <Box
          sx={{
            width: "33%",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
          className="docs-header-tab"
          onClick={() => togglePanel('docs')}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <ArticleIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Docs</Typography>
          </Box>
          {expanded === 'docs' ? <RemoveIcon /> : <AddIcon />}
        </Box>

        {/* Cart Tab */}
        <Box
          sx={{
            width: "33%",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
          className="cart-header-tab"
          onClick={() => togglePanel('cart')}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <ShoppingCartIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Cart</Typography>
          </Box>
          {expanded === 'cart' ? <RemoveIcon /> : <AddIcon />}
        </Box>
  
        {/* Chat Tab */}
        <Box
          sx={{
            width: "85px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
          className="chat-header-tab"
          onClick={() => togglePanel('chat')}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {expanded === 'chat' ? <RemoveIcon /> : <ChatIcon/>}
          </Box>
        </Box>
      </Box>
  
      {/* Overview Panel */}
      {expanded === 'overview' && (
        <Box className="panel chat-overview">
          <Box sx={{ display: "flex", width: "100%" }}>
            {/* Team Sidebar */}
            <Box
              sx={{
                width: "20%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                borderRight: "1px solid #ddd",
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Sales Team
              </Typography>
              <Box sx={{ display: "flex", mb: 2 }}>
                {salesTeam.length
                  ? salesTeam.map(renderAvatar)
                  : <Typography variant="caption">No Sales Team</Typography>}
              </Box>
  
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Purchase Team
              </Typography>
              <Box sx={{ display: "flex" }}>
                {purchaseTeam.length
                  ? purchaseTeam.map(renderAvatar)
                  : <Typography variant="caption">No Purchase Team</Typography>}
              </Box>
            </Box>
  
            {/* Progress Content */}
            <Box sx={{ width: "80%", pl: 2 }}>
              {/* Sales Order Progress */}
              <Typography variant="subtitle1">Sales Order Progress</Typography>
              <Box sx={{ position: 'relative', mt: 1 }}>
                <LinearProgress variant="determinate" value={salesProgress} />
                {salesIndex >= 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -13,
                      left: `${((salesIndex + 1) / salesStages.length) * 100}%`,
                      transform: 'translateX(-50%)',
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: 'primary.main',
                        width: 33,
                        height: 33,
                        border: '2px solid white',
                      }}
                    >
                      {salesIconMap[salesStages[salesIndex]]}
                    </Avatar>
                  </Box>
                )}
              </Box>
              <Box sx={{ mt: 0.5, display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography variant="caption" display="block">
                  {currentSoLabel}
                </Typography>
                <IconButton size="small" onClick={(event) => setSoAnchorEl(event.currentTarget)} aria-label="View sales journey details">
                  <InfoOutlinedIcon fontSize="inherit" />
                </IconButton>
              </Box>
              {renderJourneyPopover({
                anchorEl: soAnchorEl,
                onClose: () => setSoAnchorEl(null),
                label: currentSoLabel,
                tooltip: currentSoTooltip || currentSoLabel,
                actions: soQuickActions,
              })}
  
              {/* Spacer */}
              <Box mt={2} />
  
              {/* Purchase Order Progress */}
              <Typography variant="subtitle1">Purchase Order Progress</Typography>
              {purchaseOrderStatus == null ? (
                <>
                  <LinearProgress variant="determinate" value={0} className="disabled-progress" />
                  <Typography variant="caption" display="block" mt={0.5}>
                    PO stage not reached
                  </Typography>
                </>
              ) : (
                <>
                  <Box sx={{ position: 'relative', mt: 1 }}>
                    <LinearProgress variant="determinate" value={purchaseProgress} />
                    {purchaseIndex >= 0 && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -13,
                          left: `${((purchaseIndex + 1) / purchaseStages.length) * 100}%`,
                          transform: 'translateX(-50%)',
                        }}
                      >
                        <Avatar
                          sx={{
                            bgcolor: 'primary.main',
                            width: 33,
                            height: 33,
                            border: '2px solid white',
                          }}
                        >
                          {purchaseIconMap[purchaseStages[purchaseIndex]]}
                        </Avatar>
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ mt: 0.5, display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography variant="caption" display="block">
                      {currentPoLabel}
                    </Typography>
                    <IconButton size="small" onClick={(event) => setPoAnchorEl(event.currentTarget)} aria-label="View purchase journey details">
                      <InfoOutlinedIcon fontSize="inherit" />
                    </IconButton>
                  </Box>
                  {renderJourneyPopover({
                    anchorEl: poAnchorEl,
                    onClose: () => setPoAnchorEl(null),
                    label: currentPoLabel,
                    tooltip: currentPoTooltip || currentPoLabel,
                    actions: poQuickActions,
                  })}
                </>
              )}
            </Box>
          </Box>
        </Box>
      )}
  
      {/* Docs Panel */}
      {expanded === 'docs' && (
        <Box className="panel docs-panel" sx={{ p: 2 }}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              justifyContent: 'flex-start',
            }}
            className="docs-panel-inner"
          >
            {docs.map((doc) => (
              <Box
                key={doc.label}
                sx={{
                  flex: 1,
                  minWidth: 160,
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <Box
                  onClick={doc.available ? doc.onClick : undefined}
                  className="doc-panel-icon"
                  sx={{
                    position: 'relative',
                    width: 80,
                    height: 80,
                    cursor: doc.available ? 'pointer' : 'default',
                    display: "flex",
                    alignContent: "center",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "10px",
                  }}
                  data-state={doc.available ? "available" : "missing"}
                >
                  {doc.icon}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      borderRadius: "10px",
                        bgcolor: "transparent",
                      }}
                  />
                </Box>

                <Typography className="docs-panel-label" variant="body2" sx={{ mt: 1 }}>
                  {doc.label}
                </Typography>

                <Typography
                  variant="caption"
                  sx={{ color: doc.available ? 'success.main' : 'error.main' }}
                >
                  {doc.available ? 'View Link' : 'Not Available'}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* ✅ NEW: Cart Panel */}
      {expanded === 'cart' && (
        <Box className="panel cart-panel" sx={{ p: 2 }}>
          <Box className="cart-panel-inner">
            {renderCart ? renderCart : (
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                No cart content provided.
              </Typography>
            )}
          </Box>
        </Box>
      )}
  
      {/* Chat Panel */}
      {expanded === 'chat' && (
        <Box className="panel chat-panel">
          <ChatBox orderId={orderId} assignedUsers={assignedUsers} allUsers={allUsers} notes={notesForChat}/>
        </Box>
      )}
    </Box>
  );
}

export default OrderProgressBar;
